import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkle, ArrowsClockwise, PencilSimple, Trash, Check, X } from "@phosphor-icons/react";

/**
 * Debrief AI Summary card — appears in ChantierDetail overview.
 * Shows: (1) generate button when no summary yet, (2) preview + edit + regenerate when one exists.
 * The summary text is what appears in the client PDF (section "Compte-rendu de chantier"),
 * replacing the raw debrief transcript list.
 */
export default function DebriefSummaryCard({ chantier, onChanged }) {
  const summary = chantier?.debrief_summary || null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(summary?.text || "");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/chantiers/${chantier.id}/debrief-summary`);
      setDraft(r.data.text);
      toast.success("Résumé généré");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const startEdit = () => { setDraft(summary?.text || ""); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(summary?.text || ""); };

  const save = async () => {
    const t = (draft || "").trim();
    if (!t) { toast.error("Texte vide"); return; }
    setBusy(true);
    try {
      await api.patch(`/chantiers/${chantier.id}/debrief-summary`, { text: t });
      toast.success("Résumé enregistré");
      setEditing(false);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Effacer le résumé ? Le PDF affichera les debriefs bruts à la place.")) return;
    setBusy(true);
    try {
      await api.delete(`/chantiers/${chantier.id}/debrief-summary`);
      setDraft("");
      toast.success("Résumé effacé");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2" data-testid="debrief-summary-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkle size={14} weight="duotone" className="text-[#7CB2FF]" />
            Résumé IA des debriefs
          </div>
          <div className="text-[10px] text-white/50 mt-0.5">
            Compte-rendu synthétique inséré dans le PDF client à la place des debriefs bruts.
          </div>
        </div>
        {summary && !editing && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={startEdit} className="text-white/40 hover:text-[#7CB2FF] p-1" title="Éditer" data-testid="debrief-summary-edit-btn"><PencilSimple size={13} /></button>
            <button onClick={generate} disabled={busy} className="text-white/40 hover:text-[#7CB2FF] p-1" title="Régénérer" data-testid="debrief-summary-regen-btn"><ArrowsClockwise size={13} /></button>
            <button onClick={clear} disabled={busy} className="text-white/40 hover:text-red-400 p-1" title="Effacer" data-testid="debrief-summary-clear-btn"><Trash size={13} /></button>
          </div>
        )}
      </div>

      {!summary && !editing && (
        <Button onClick={generate} disabled={busy} className="w-full bg-white/5 hover:bg-[#005CFF]/20 border border-white/10 hover:border-[#005CFF]/50 h-9 text-white" data-testid="debrief-summary-generate-btn">
          {busy ? (
            <><div className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin mr-2" />Rédaction en cours…</>
          ) : (
            <><Sparkle size={13} weight="fill" className="mr-1.5 text-[#7CB2FF]" />Résumer les debriefs (IA)</>
          )}
        </Button>
      )}

      {summary && !editing && (
        <div className="rounded-lg bg-black/30 border border-white/5 p-3 text-sm text-white/85 whitespace-pre-wrap leading-relaxed" data-testid="debrief-summary-text">
          {summary.text}
          <div className="text-[9px] text-white/30 mt-2 font-mono">
            {summary.edited ? "Édité" : "Généré par IA"} · {(summary.edited ? summary.edited_at : summary.generated_at || "").slice(0, 16).replace("T", " ")}
            {(summary.edited ? summary.edited_by : summary.generated_by) && ` · ${summary.edited ? summary.edited_by : summary.generated_by}`}
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="bg-black/30 border-white/10 min-h-[130px] text-sm leading-relaxed"
            data-testid="debrief-summary-editor"
          />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={cancelEdit} className="flex-1 border border-white/10 h-8" data-testid="debrief-summary-cancel-btn">
              <X size={12} className="mr-1" /> Annuler
            </Button>
            <Button onClick={save} disabled={busy} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC] h-8" data-testid="debrief-summary-save-btn">
              <Check size={12} weight="bold" className="mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
